/**
 * Перенос `presentation/providers/language_provider.dart`.
 *
 * Riverpod в порте нет: `NotifierProvider<LanguageNotifier, AppLanguage?>`
 * стал хранилищем zustand. Соответствие вызовов:
 *
 *   `ref.watch(languageProvider)`          -> `useLanguage()`
 *   `ref.read(languageProvider.notifier)`  -> `languageStore.getState()`
 *
 * Отличие от Dart: там язык переводов менялся сам — `MaterialApp.locale`
 * читал состояние провайдера (`language?.locale ?? defaultLanguage.locale`).
 * У i18next своё состояние, поэтому язык переключается явно — здесь же,
 * рядом с состоянием, а не на стороне вызывающего.
 */

import { create } from 'zustand';

import { defaultLanguage, type AppLanguage } from '@/domain/models/appLanguage';
import { setL10nLanguage } from '@/l10n/useL10n';
import { StorageHelper } from '@/utils/storageHelper';

/** Состояние и действия хранилища — то же, что было у `LanguageNotifier`. */
export interface LanguageStore {
  /** `ref.watch(languageProvider)`; `null` — язык ещё не выбирали. */
  readonly language: AppLanguage | null;

  /**
   * Замена `languageProvider.overrideWith(() => LanguageNotifier(initial))`
   * из `main.dart`: язык, поднятый из хранилища при старте.
   */
  readonly initLanguage: (initialLanguage: AppLanguage | null) => Promise<void>;

  readonly setLanguage: (language: AppLanguage) => Promise<void>;
}

/** Хранилище языка интерфейса. */
export const useLanguageStore = create<LanguageStore>()((set) => {
  return {
    language: null,

    initLanguage: async (initialLanguage: AppLanguage | null): Promise<void> => {
      set({ language: initialLanguage });
      // `locale: language?.locale ?? AppLanguage.defaultLanguage.locale`
      // из `MainApp`: языка нет — интерфейс на языке по умолчанию.
      await setL10nLanguage(initialLanguage ?? defaultLanguage);
    },

    setLanguage: async (language: AppLanguage): Promise<void> => {
      await StorageHelper.instance.saveLanguage(language);
      // Переводы переключаются до `set`, чтобы `getL10n()` вне React отдавал
      // новый язык уже тем, кого разбудит смена состояния.
      await setL10nLanguage(language);
      set({ language: language });
    },
  };
});

/**
 * Хранилище вне React — тот же объект, что и [useLanguageStore]; отдельное
 * имя, чтобы вызов вне компонента не выглядел вызовом хука.
 */
export const languageStore = useLanguageStore;

/** `ref.watch(languageProvider)` — перерисовка при смене языка. */
export function useLanguage(): AppLanguage | null {
  return useLanguageStore((store) => store.language);
}
